import { useEffect, useState } from "react";
import { Check, Download, FileText, Plus, Receipt, Trash2, X } from "lucide-react";
import { jsPDF } from "jspdf";
import api from "../lib/api";
import { money, moneyFull, todayISO, shortDate } from "../lib/format";
import { notify } from "../lib/toast";
import { useAuth } from "../context/AuthContext";

function InvoiceModal({ open, onClose, onSave, contacts, accounts }) {
  const empty = () => ({ _key: Math.random().toString(36).slice(2, 9), description: "", quantity: 1, unit_price: 0, tax_rate: 20 });
  const [form, setForm] = useState({
    contact_id: "",
    account_id: "",
    issue_date: todayISO(),
    due_date: todayISO(),
    notes: "",
    items: [empty()],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        contact_id: contacts[0]?.id || "",
        account_id: accounts[0]?.id || "",
        issue_date: todayISO(),
        due_date: todayISO(),
        notes: "",
        items: [empty()],
      });
      setError("");
    }
    // eslint-disable-next-line
  }, [open, contacts, accounts]);

  if (!open) return null;

  const setItem = (i, patch) => {
    const items = form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, empty()] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const tax = form.items.reduce(
    (s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0) * (Number(it.tax_rate || 0) / 100),
    0,
  );
  const total = subtotal + tax;

  const submit = async () => {
    if (!form.contact_id) return setError("Cari seçin");
    if (form.items.some((it) => !it.description.trim() || !Number(it.unit_price))) {
      return setError("Tüm kalemlerde açıklama ve tutar gerekli");
    }
    setBusy(true);
    setError("");
    try {
      await onSave({
        contact_id: form.contact_id,
        account_id: form.account_id || null,
        issue_date: form.issue_date,
        due_date: form.due_date,
        notes: form.notes,
        items: form.items.map((it) => ({
          description: it.description.trim(),
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price),
          tax_rate: Number(it.tax_rate) || 0,
        })),
      });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="invoice-modal">
      <div className="modal invoice-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">YENİ FATURA</span>
            <h2>Fatura oluştur</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-invoice-modal">
            <X size={18} />
          </button>
        </div>

        <div className="form-grid">
          <label>
            Cari
            <select
              value={form.contact_id}
              onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
              data-testid="invoice-contact-select"
            >
              <option value="">Seçin…</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label>
            Tahsilat hesabı
            <select
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              data-testid="invoice-account-select"
            >
              <option value="">Seçilmedi</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.bank} · {a.last_four}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="form-grid">
          <label>
            Fatura tarihi
            <input
              type="date"
              value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              data-testid="invoice-issue-date"
            />
          </label>
          <label>
            Vade tarihi
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              data-testid="invoice-due-date"
            />
          </label>
        </div>

        <div className="invoice-items">
          <div className="items-head">
            <span>KALEMLER</span>
            <button className="secondary-btn" onClick={addItem} data-testid="add-invoice-item">
              <Plus size={14} /> Kalem ekle
            </button>
          </div>
          {form.items.map((it, i) => (
            <div className="item-row" key={it._key} data-testid={`invoice-item-${i}`}>
              <input
                placeholder="Açıklama"
                value={it.description}
                onChange={(e) => setItem(i, { description: e.target.value })}
                data-testid={`item-desc-${i}`}
              />
              <input
                type="number"
                placeholder="Adet"
                value={it.quantity}
                onChange={(e) => setItem(i, { quantity: e.target.value })}
                data-testid={`item-qty-${i}`}
              />
              <input
                type="number"
                placeholder="Birim ₺"
                value={it.unit_price}
                onChange={(e) => setItem(i, { unit_price: e.target.value })}
                data-testid={`item-price-${i}`}
              />
              <input
                type="number"
                placeholder="KDV %"
                value={it.tax_rate}
                onChange={(e) => setItem(i, { tax_rate: e.target.value })}
                data-testid={`item-tax-${i}`}
              />
              <button
                className="icon-btn ghost"
                onClick={() => removeItem(i)}
                disabled={form.items.length === 1}
                data-testid={`remove-item-${i}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        <div className="invoice-totals">
          <div>
            <span>Ara toplam</span>
            <strong>{money(subtotal)}</strong>
          </div>
          <div>
            <span>KDV</span>
            <strong>{money(tax)}</strong>
          </div>
          <div className="total">
            <span>Genel toplam</span>
            <strong>{moneyFull(total)}</strong>
          </div>
        </div>

        <label>
          Not
          <input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Fatura notu…"
            data-testid="invoice-notes"
          />
        </label>

        {error ? <div className="form-error">{error}</div> : null}

        <button className="primary-btn full" onClick={submit} disabled={busy} data-testid="save-invoice-button">
          <FileText size={17} /> {busy ? "Kaydediliyor…" : "Faturayı oluştur"}
        </button>
      </div>
    </div>
  );
}

function generatePDF(inv, companyName) {
  const doc = new jsPDF();
  doc.setFont("helvetica");

  // Header
  doc.setFontSize(22);
  doc.setTextColor(30, 41, 59);
  doc.text("FATURA", 20, 22);

  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(companyName || "VeraFin", 20, 30);

  // Number & date
  doc.setFontSize(11);
  doc.setTextColor(30, 41, 59);
  doc.text(`No: ${inv.number}`, 150, 22);
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Tarih: ${inv.issue_date}`, 150, 28);
  doc.text(`Vade: ${inv.due_date}`, 150, 33);

  // Client
  doc.setDrawColor(200);
  doc.line(20, 45, 190, 45);
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text("MUSTERI", 20, 52);
  doc.setFontSize(12);
  doc.setTextColor(30, 41, 59);
  doc.text(inv.contact_name, 20, 60);

  // Items header
  let y = 78;
  doc.setFillColor(241, 245, 249);
  doc.rect(20, y - 5, 170, 8, "F");
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text("Aciklama", 22, y);
  doc.text("Adet", 110, y);
  doc.text("Birim", 130, y);
  doc.text("KDV %", 152, y);
  doc.text("Tutar", 178, y, { align: "right" });

  y += 10;
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59);
  inv.items.forEach((it) => {
    const rowTotal = it.quantity * it.unit_price * (1 + (it.tax_rate || 0) / 100);
    // ASCII-fold description to keep jsPDF core-font safe
    const desc = (it.description || "").replace(/[^\x20-\x7E]/g, (ch) => {
      const map = { ı: "i", İ: "I", ş: "s", Ş: "S", ğ: "g", Ğ: "G", ü: "u", Ü: "U", ö: "o", Ö: "O", ç: "c", Ç: "C" };
      return map[ch] || ch;
    });
    doc.text(desc.slice(0, 55), 22, y);
    doc.text(String(it.quantity), 110, y);
    doc.text(it.unit_price.toFixed(2), 130, y);
    doc.text(`%${it.tax_rate}`, 152, y);
    doc.text(rowTotal.toFixed(2) + " TL", 188, y, { align: "right" });
    y += 7;
  });

  // Totals
  y += 6;
  doc.setDrawColor(220);
  doc.line(120, y, 190, y);
  y += 6;
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text("Ara toplam", 130, y);
  doc.setTextColor(30, 41, 59);
  doc.text(inv.subtotal.toFixed(2) + " TL", 188, y, { align: "right" });
  y += 6;
  doc.setTextColor(100);
  doc.text("KDV", 130, y);
  doc.setTextColor(30, 41, 59);
  doc.text(inv.tax.toFixed(2) + " TL", 188, y, { align: "right" });
  y += 8;
  doc.setFontSize(12);
  doc.setTextColor(30, 41, 59);
  doc.text("GENEL TOPLAM", 130, y);
  doc.text(inv.total.toFixed(2) + " TL", 188, y, { align: "right" });

  // Status stamp
  if (inv.status === "paid") {
    doc.setFontSize(30);
    doc.setTextColor(16, 185, 129);
    doc.text("ODENDI", 90, 150, { angle: 15 });
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text("VeraFin ile olusturuldu · verafin.com", 20, 285);

  doc.save(`fatura-${inv.number}.pdf`);
}

export default function InvoicesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [modal, setModal] = useState(false);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, c, a] = await Promise.all([
        api.get("/invoices"),
        api.get("/contacts"),
        api.get("/accounts"),
      ]);
      setItems(inv.data);
      setContacts(c.data);
      setAccounts(a.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((i) => (filter === "all" ? true : i.status === filter));
  const draft = items.filter((i) => i.status === "draft").reduce((s, i) => s + i.total, 0);
  const paid = items.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);

  const markPaid = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/pay`);
      notify.success(`${inv.number} tahsil edildi`);
      load();
    } catch {
      notify.error("Ödeme kaydı başarısız");
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`${inv.number} silinsin mi?`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      notify.success("Fatura silindi");
      load();
    } catch {
      notify.error("Silme başarısız");
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">FATURALAR</div>
          <h1>Faturalar</h1>
          <p>Cari müşterilerinize fatura oluşturun, PDF indirin ve tek tıkla tahsil edin.</p>
        </div>
        <div className="head-actions">
          <button
            className="primary-btn"
            onClick={() => setModal(true)}
            disabled={contacts.length === 0}
            data-testid="add-invoice-button"
          >
            <Plus size={17} /> Fatura oluştur
          </button>
        </div>
      </div>

      <div className="account-summary panel">
        <div>
          <span>Bekleyen tutar</span>
          <strong className="expense-text">{money(draft)}</strong>
        </div>
        <div>
          <span>Tahsil edilen</span>
          <strong className="income-text">{money(paid)}</strong>
        </div>
        <div>
          <span>Fatura sayısı</span>
          <strong>{items.length}</strong>
        </div>
      </div>

      <div className="panel inner-page" data-testid="invoices-panel">
        <div className="panel-head filter-head">
          <div className="segmented sm">
            <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")} data-testid="invoices-filter-all">Tümü</button>
            <button className={filter === "draft" ? "active" : ""} onClick={() => setFilter("draft")} data-testid="invoices-filter-draft">Bekleyen</button>
            <button className={filter === "paid" ? "active" : ""} onClick={() => setFilter("paid")} data-testid="invoices-filter-paid">Ödenen</button>
          </div>
        </div>

        {loading ? (
          <div className="page-loading">Yükleniyor…</div>
        ) : contacts.length === 0 ? (
          <div className="empty-state">
            <Receipt size={30} />
            <h3>Önce bir cari ekleyin</h3>
            <p>Fatura oluşturmak için en az bir müşteri gereklidir.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" data-testid="invoices-empty">
            <Receipt size={30} />
            <h3>Fatura yok</h3>
            <p>İlk faturanızı oluşturarak başlayın.</p>
            <button className="primary-btn" onClick={() => setModal(true)} data-testid="empty-add-invoice-btn">
              <Plus size={14} /> Fatura oluştur
            </button>
          </div>
        ) : (
          <div className="invoice-list">
            {filtered.map((inv) => (
              <div className={`invoice-row ${inv.status}`} key={inv.id} data-testid={`invoice-${inv.id}`}>
                <div className={`inv-badge ${inv.status}`}>
                  <Receipt size={14} />
                </div>
                <div className="inv-main">
                  <div className="inv-title">
                    <strong>{inv.number}</strong>
                    <span className={`status-chip ${inv.status}`}>
                      {inv.status === "paid" ? "Ödendi" : "Bekliyor"}
                    </span>
                  </div>
                  <span>{inv.contact_name} · Vade {shortDate(inv.due_date)}</span>
                </div>
                <div className="inv-amount">{money(inv.total)}</div>
                <div className="inv-actions">
                  <button
                    className="icon-btn"
                    onClick={() => generatePDF(inv, user?.company_name)}
                    data-testid={`pdf-invoice-${inv.id}`}
                    title="PDF indir"
                  >
                    <Download size={14} />
                  </button>
                  {inv.status === "draft" ? (
                    <button
                      className="icon-btn pay"
                      onClick={() => markPaid(inv)}
                      data-testid={`pay-invoice-${inv.id}`}
                      title="Ödendi olarak işaretle"
                    >
                      <Check size={14} />
                    </button>
                  ) : null}
                  <button
                    className="icon-btn ghost"
                    onClick={() => remove(inv)}
                    data-testid={`delete-invoice-${inv.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <InvoiceModal
        open={modal}
        onClose={() => setModal(false)}
        onSave={async (payload) => {
          await api.post("/invoices", payload);
          notify.success("Fatura oluşturuldu");
          await load();
        }}
        contacts={contacts}
        accounts={accounts}
      />
    </>
  );
}
